/**
 * @file theme-toggle.tsx
 */

"use client";

import { useTheme } from "next-themes";
import { useState, useEffect } from "react";
import { cn } from "visuais/fmt";
import { motion } from "motion/react";
import { Eclipse, Moon, Sun } from "lucide-react";

interface ThemeSwitcherProps {
  onThemeChange?: () => void;
}

/**
 * @returns {JSX.Element} Alternador de tema.
 * @example
 * ```tsx
 * <ThemeSwitcher onThemeChange={() => console.log("Tema alterado")} />
 * ```
 */
export function ThemeSwitcher({ onThemeChange }: ThemeSwitcherProps) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const getPosition = () => {
    switch (theme) {
      case "light":
        return "0rem";
      case "system":
        return "2rem";
      case "dark":
        return "4rem";
      default:
        return "0rem";
    }
  };

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme);
    if (onThemeChange) onThemeChange();
  };

  return (
    <div
      className={cn(
        "relative flex items-center p-1 rounded-full border border-primary bg-background shadow-md h-10"
      )}
    >
      <motion.div
        className="absolute top-[0.2rem] left-1 size-8 rounded-full bg-primary"
        animate={{ x: getPosition() }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      />
      <button
        className={cn(
          "relative z-10 flex items-center justify-center size-8 rounded-full text-muted-foreground",
          theme === "light" && "text-foreground"
        )}
        onClick={() => handleThemeChange("light")}
        aria-label="Light Mode"
      >
        <Sun className="size-5" />
      </button>
      <button
        className={cn(
          "relative z-10 flex items-center justify-center size-8 rounded-full text-muted-foreground",
          theme === "system" && "text-foreground"
        )}
        onClick={() => handleThemeChange("system")}
        aria-label="System Theme"
      >
        <Eclipse className="size-5" />
      </button>
      <button
        className={cn(
          "relative z-10 flex items-center justify-center size-8 rounded-full text-muted-foreground",
          theme === "dark" && "text-foreground"
        )}
        onClick={() => handleThemeChange("dark")}
        aria-label="Dark Mode"
      >
        <Moon className="size-5" />
      </button>
    </div>
  );
}
